/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Asset } from '../models/Asset';
import type { Collection } from '../models/Collection';
import type { CollectionSpecificTraitResponse } from '../models/CollectionSpecificTraitResponse';
import type { CollectionTraitsResponse } from '../models/CollectionTraitsResponse';
import type { GetAssetsInput } from '../models/GetAssetsInput';
import type { RoutesAssetRarityResponse } from '../models/RoutesAssetRarityResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class CollectionAndAssetDataService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get asset data (multiple assets)
     * Returns key data (e.g., metadata, preview image, collection name) on multiple assets with single call
     * @returns Asset OK
     * @throws ApiError
     */
    public getAssets({
        network = 'ethereum-mainnet',
        fallbackToChain,
        requestBody,
    }: {
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** If true, any missing data will be fetched from the blockchain node, this can be slow and should not be used for performance-sensitive cases **/
        fallbackToChain?: boolean | null,
        requestBody?: GetAssetsInput,
    }): CancelablePromise<Array<Asset>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/v1/{network}/assets',
            path: {
                'network': network,
            },
            query: {
                'fallbackToChain': fallbackToChain,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation Error`,
            },
        });
    }

    /**
     * Get NFT collection data
     * Returns key data (e.g., name, preview image, symbol, creator) for a collection
     * @returns Collection OK
     * @throws ApiError
     */
    public getCollection({
        address = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        network = 'ethereum-mainnet',
        refresh,
    }: {
        /** Collection Address **/
        address?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** If true then refresh the asset's metadata and media **/
        refresh?: boolean | null,
    }): CancelablePromise<Collection> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/v1/{network}/{address}',
            path: {
                'address': address,
                'network': network,
            },
            query: {
                'refresh': refresh,
            },
            errors: {
                303: `collection not found on this network but available on another network`,
                400: `Validation Error`,
            },
        });
    }

    /**
     * Get collection traits
     * Returns list of traits of assets in collection
     * @returns CollectionTraitsResponse OK
     * @throws ApiError
     */
    public getAllTraitsForCollection({
        address = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        network = 'ethereum-mainnet',
        limit,
        offset,
    }: {
        /** Collection Address **/
        address?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        limit?: number | null,
        offset?: number | null,
    }): CancelablePromise<CollectionTraitsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/v1/{network}/{address}/traits',
            path: {
                'address': address,
                'network': network,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
            errors: {
                400: `Validation Error`,
            },
        });
    }

    /**
     * Get distribution of attributes for a particular trait
     * Returns all attribute values for a particular trait within a collection
     * @returns CollectionSpecificTraitResponse OK
     * @throws ApiError
     */
    public getAttributesForTraitWithinCollection({
        address = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        network = 'ethereum-mainnet',
        trait = 'Mouth',
        limit,
        offset,
    }: {
        /** Collection Address **/
        address?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        trait?: string,
        limit?: number | null,
        offset?: number | null,
    }): CancelablePromise<CollectionSpecificTraitResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/v1/{network}/{address}/traits/{trait}',
            path: {
                'address': address,
                'network': network,
                'trait': trait,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
            errors: {
                400: `Validation Error`,
            },
        });
    }

    /**
     * Get asset data (single asset)
     * Returns key data (e.g., metadata, preview image, collection name) for given asset
     * @returns Asset OK
     * @throws ApiError
     */
    public getAsset({
        address = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        network = 'ethereum-mainnet',
        tokenId = '1',
        refresh,
    }: {
        /** Collection Address **/
        address?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Token ID **/
        tokenId?: string,
        /** If true then refresh the asset's metadata and media **/
        refresh?: boolean | null,
    }): CancelablePromise<Asset> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/v1/{network}/{address}/{tokenID}',
            path: {
                'address': address,
                'network': network,
                'tokenID': tokenId,
            },
            query: {
                'refresh': refresh,
            },
            errors: {
                400: `Validation Error`,
                422: `network ID is invalid or asset does not have associated metadata`,
                504: `Failed to fetch data from the external site before timeout`,
            },
        });
    }

    /**
     * Get asset rarity
     * Returns rarity (ranking and score) of an asset in a given collection
     * @returns RoutesAssetRarityResponse OK
     * @throws ApiError
     */
    public getAssetRarity({
        address = '0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D',
        network = 'ethereum-mainnet',
        tokenId = '1',
    }: {
        /** Collection Address **/
        address?: string,
        /** Network ID **/
        network?: 'arbitrum-mainnet' | 'avalanche-mainnet' | 'bsc-mainnet' | 'celo-mainnet' | 'ethereum-goerli' | 'ethereum-mainnet' | 'ethereum-rinkeby' | 'ethereum-ropsten' | 'fantom-mainnet' | 'harmony-mainnet' | 'optimism-mainnet' | 'polygon-mainnet' | 'solana-mainnet',
        /** Token ID **/
        tokenId?: string,
    }): CancelablePromise<RoutesAssetRarityResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/v1/{network}/{address}/{tokenID}/rarity',
            path: {
                'address': address,
                'network': network,
                'tokenID': tokenId,
            },
            errors: {
                400: `Validation Error`,
            },
        });
    }

}